import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { track } from "../../lib/analytics";

/**
 * Reusable investor inquiry / consultation / guide form.
 * variant: "inquiry" | "consultation" | "guide" | "opportunity" | "partner"
 */
export default function LeadForm({
  variant = "inquiry", sourcePage, opportunity, defaultMarket,
  compact = false, testIdPrefix = "lead-form", onSuccess,
}) {
  const settings = useSettings();
  const markets = settings?.get("market") || [];
  const investmentCats = settings?.get("investment_category") || [];

  const [f, setF] = useState({
    name: "", email: "", phone: "", country: "",
    preferred_contact: "email",
    primary_market: defaultMarket || "",
    secondary_market: "",
    investment_category: "",
    budget_range: "",
    timeline: "",
    heard_about: "",
    message: "",
    consent: false,
    // guide-specific
    first_name: "", last_name: "",
    // partner-specific
    company: "", partner_type: "", experience: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (variant === "guide") {
        const { data } = await api.post("/public/guide-requests", {
          first_name: f.first_name || f.name?.split(" ")[0] || "Investor",
          last_name: f.last_name || f.name?.split(" ").slice(1).join(" "),
          email: f.email, primary_market: f.primary_market, consent: true,
        });
        setOk(data);
        toast.success("Guide sent! Check your inbox for the download link.");
      } else if (variant === "consultation") {
        const { data } = await api.post("/public/consultation-requests", {
          name: f.name, email: f.email, phone: f.phone, country: f.country,
          market: f.primary_market, type: "video", message: f.message, source_page: sourcePage,
        });
        setOk(data);
        toast.success("Inquiry submitted. Our team will follow up shortly.");
      } else if (variant === "opportunity") {
        const { data } = await api.post("/public/opportunity-inquiries", {
          name: f.name, email: f.email, phone: f.phone,
          opportunity_id: opportunity?.id, opportunity_title: opportunity?.title,
          message: f.message, source_page: sourcePage,
        });
        setOk(data);
        toast.success("Inquiry submitted. An advisor will follow up.");
      } else if (variant === "partner") {
        const { data } = await api.post("/public/partner-applications", {
          name: f.name, email: f.email, phone: f.phone, country: f.country,
          company: f.company, partner_type: f.partner_type, experience: f.experience,
          message: f.message, consent: f.consent,
        });
        setOk(data);
        toast.success("Application received. Welcome to Global Link.");
      } else {
        // investor inquiry (default)
        const { data } = await api.post("/public/inquiries", {
          name: f.name, email: f.email, phone: f.phone, country: f.country,
          preferred_contact: f.preferred_contact,
          primary_market: f.primary_market, secondary_market: f.secondary_market,
          investment_category: f.investment_category,
          budget_range: f.budget_range, timeline: f.timeline,
          heard_about: f.heard_about, message: f.message, consent: f.consent,
          source: "website_contact", source_page: sourcePage,
        });
        setOk(data);
        toast.success("Inquiry submitted. Our team will follow up shortly.");
      }
      onSuccess?.();
      track.formSubmit(variant, { source_page: sourcePage || null });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const T = (id) => `${testIdPrefix}-${id}`;
  const field = "w-full rounded-md border border-altura-espresso/15 bg-white px-3.5 py-2.5 text-sm text-altura-charcoal placeholder:text-altura-charcoal/40 focus:border-altura-gold focus:outline-none focus:ring-1 focus:ring-altura-gold/50";
  const label = "text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60";

  if (ok) {
    return (
      <div className="rounded-lg border border-altura-gold/30 bg-altura-gold/5 p-6 text-altura-espresso" data-testid={T("success")}>
        <div className="eyebrow text-altura-gold-dark">Thank you</div>
        <h3 className="mt-2 font-editorial text-2xl">We've received your details.</h3>
        <p className="mt-3 text-sm text-altura-charcoal/70">
          An Altura Global advisor will reach out to you soon. In the meantime, feel free to explore our Ghana flagship market and Learning Center.
        </p>
        {variant === "guide" && ok.download_url && (
          <a href={ok.download_url} download className="btn-espresso mt-5" data-testid={T("download")}>Download Guide</a>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={T("form")}>
      {variant === "guide" ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className={label}>First name</label><input required className={field} value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid={T("first-name")} /></div>
            <div><label className={label}>Last name</label><input className={field} value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid={T("last-name")} /></div>
          </div>
          <div><label className={label}>Email</label><input type="email" required className={field} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid={T("email")} /></div>
          <div>
            <label className={label}>Primary market of interest</label>
            <select className={field} value={f.primary_market} onChange={(e) => set("primary_market", e.target.value)} data-testid={T("market")}>
              <option value="">Any / undecided</option>
              {markets.map((m) => <option key={m.key} value={m.label}>{m.label}</option>)}
            </select>
          </div>
        </>
      ) : variant === "partner" ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className={label}>Full name</label><input required className={field} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid={T("name")} /></div>
            <div><label className={label}>Company</label><input className={field} value={f.company} onChange={(e) => set("company", e.target.value)} data-testid={T("company")} /></div>
            <div><label className={label}>Email</label><input type="email" required className={field} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid={T("email")} /></div>
            <div><label className={label}>Phone</label><input className={field} value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid={T("phone")} /></div>
            <div><label className={label}>Country</label><input className={field} value={f.country} onChange={(e) => set("country", e.target.value)} data-testid={T("country")} /></div>
            <div>
              <label className={label}>Partner type</label>
              <select className={field} value={f.partner_type} onChange={(e) => set("partner_type", e.target.value)} data-testid={T("partner-type")}>
                <option value="">Select…</option>
                <option>Referral Partner</option><option>Developer</option><option>Agent / Brokerage</option>
                <option>Legal Professional</option><option>Financial Professional</option>
                <option>Relocation Partner</option><option>Marketing Partner</option><option>Other</option>
              </select>
            </div>
          </div>
          <div><label className={label}>Relevant experience</label><textarea rows={3} className={field} value={f.experience} onChange={(e) => set("experience", e.target.value)} data-testid={T("experience")} /></div>
          <div><label className={label}>Notes</label><textarea rows={3} className={field} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid={T("message")} /></div>
        </>
      ) : (
        <>
          <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-3`}>
            <div><label className={label}>Full name</label><input required className={field} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid={T("name")} /></div>
            <div><label className={label}>Email</label><input type="email" required className={field} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid={T("email")} /></div>
            <div><label className={label}>Phone</label><input className={field} value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid={T("phone")} /></div>
            <div><label className={label}>Country of residence</label><input className={field} value={f.country} onChange={(e) => set("country", e.target.value)} data-testid={T("country")} /></div>
            {variant !== "consultation" && variant !== "opportunity" && (
              <div>
                <label className={label}>Preferred contact</label>
                <select className={field} value={f.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)} data-testid={T("preferred-contact")}>
                  <option value="email">Email</option><option value="phone">Phone</option><option value="whatsapp">WhatsApp</option>
                </select>
              </div>
            )}
            <div>
              <label className={label}>Primary market</label>
              <select className={field} value={f.primary_market} onChange={(e) => set("primary_market", e.target.value)} data-testid={T("market")}>
                <option value="">Select…</option>
                {markets.map((m) => <option key={m.key} value={m.label}>{m.label}</option>)}
              </select>
            </div>
            {variant === "inquiry" && (
              <>
                <div>
                  <label className={label}>Secondary market</label>
                  <select className={field} value={f.secondary_market} onChange={(e) => set("secondary_market", e.target.value)} data-testid={T("secondary-market")}>
                    <option value="">—</option>
                    {markets.map((m) => <option key={m.key} value={m.label}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Investment category</label>
                  <select className={field} value={f.investment_category} onChange={(e) => set("investment_category", e.target.value)} data-testid={T("category")}>
                    <option value="">Select…</option>
                    {investmentCats.map((c) => <option key={c.key} value={c.label}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Budget range (USD)</label>
                  <select className={field} value={f.budget_range} onChange={(e) => set("budget_range", e.target.value)} data-testid={T("budget")}>
                    <option value="">Select…</option>
                    <option>Under $250K</option><option>$250K–$500K</option><option>$500K–$1M</option>
                    <option>$1M–$2.5M</option><option>$2.5M–$5M</option><option>$5M+</option>
                  </select>
                </div>
                <div>
                  <label className={label}>Timeline</label>
                  <select className={field} value={f.timeline} onChange={(e) => set("timeline", e.target.value)} data-testid={T("timeline")}>
                    <option value="">Select…</option>
                    <option>Exploring · 12+ months</option><option>Planning · 6–12 months</option>
                    <option>Active · 3–6 months</option><option>Immediate · 0–3 months</option>
                  </select>
                </div>
                <div>
                  <label className={label}>How did you hear about us?</label>
                  <input className={field} value={f.heard_about} onChange={(e) => set("heard_about", e.target.value)} data-testid={T("heard-about")} />
                </div>
              </>
            )}
          </div>
          <div>
            <label className={label}>{variant === "opportunity" ? "Your question" : "Message"}</label>
            <textarea rows={4} className={field} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder={variant === "opportunity" ? "Tell us what you'd like to know…" : "Tell us about your investment objectives…"} data-testid={T("message")} />
          </div>
          {variant === "inquiry" && (
            <label className="flex items-start gap-2 text-xs text-altura-charcoal/70">
              <input type="checkbox" className="mt-0.5" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} data-testid={T("consent")} />
              <span>I consent to be contacted by Altura Global regarding my inquiry and understand my data will be handled according to Altura's privacy practices.</span>
            </label>
          )}
        </>
      )}

      <button type="submit" disabled={submitting} className="btn-gold w-full sm:w-auto disabled:opacity-60" data-testid={T("submit")}>
        {submitting ? "Sending…" :
          variant === "guide" ? "Send Me the Guide" :
          variant === "consultation" ? "Submit Inquiry" :
          variant === "opportunity" ? "Send Inquiry" :
          variant === "partner" ? "Submit Application" :
          "Submit Inquiry"}
      </button>
    </form>
  );
}
